import { dayjs } from "utils";

/**
 * 日期格式化函数类型
 * @param timestamp 时间戳
 * @param dateKey 原始日期字符串（根据 dateFormat 格式化）
 * @returns 格式化后的分组 key
 */
export type DateGroupFormatter = (timestamp: number, dateKey: string) => string;

/**
 * 默认的日期分组格式化函数
 * 今天显示 "Today"，昨天显示 "Yesterday"，其他日期显示 "MMM DD, YYYY" 格式
 *
 * @param timestamp 时间戳
 * @param _dateKey 原始日期字符串（未使用）
 * @returns 格式化后的日期字符串
 *
 * @example
 * formatDateGroup(Date.now(), '2024-11-24') // "Today"
 * formatDateGroup(Date.now() - 86400000, '2024-11-23') // "Yesterday"
 * formatDateGroup(Date.now() - 172800000, '2024-11-22') // "Nov 22, 2024"
 */
export function formatDateGroup(timestamp: number, _dateKey: string): string {
  const date = dayjs(timestamp);
  const today = dayjs().startOf("day");
  const yesterday = today.subtract(1, "day");

  // 判断是否为今天
  if (date.isSame(today, "day")) {
    return "Today";
  }

  // 判断是否为昨天
  if (date.isSame(yesterday, "day")) {
    return "Yesterday";
  }

  // 其他日期使用 "MMM DD, YYYY" 格式
  return date.format("MMM DD, YYYY");
}

/**
 * 按日期分组数据的通用工具函数
 * 将数据按照指定时间戳字段是同一天的分成一组
 *
 * @template T 数据项类型
 * @param data 要分组的数据数组
 * @param timestampField 时间戳字段名，默认为 'created'
 * @param dateFormat 日期格式，默认为 'YYYY-MM-DD'，用作分组的 key
 * @param formatGroupKey 自定义分组 key 的格式化函数，如果提供则会替代默认的 dateFormat
 * @returns 按日期分组的数据对象，key 为日期字符串，value 为该日期的数据数组
 *
 * @example
 * ```typescript
 * interface Report {
 *   id: string;
 *   created: number;
 *   content: string;
 * }
 *
 * const reports: Report[] = [
 *   { id: '1', created: 1700000000000, content: 'Report 1' },
 *   { id: '2', created: 1700086400000, content: 'Report 2' },
 *   { id: '3', created: 1700000000000, content: 'Report 3' },
 * ];
 *
 * // 使用默认格式
 * const grouped = groupByDate(reports);
 * // 结果: { '2023-11-15': [report1, report3], '2023-11-16': [report2] }
 *
 * // 使用自定义格式化函数
 * const groupedWithCustomFormat = groupByDate(reports, 'created', 'YYYY-MM-DD', formatDateGroup);
 * // 结果: { 'Today': [...], 'Yesterday': [...], 'Nov 22, 2024': [...] }
 * ```
 */
export function groupByDate<T extends Record<string, any>>(
  data: T[] | undefined,
  timestampField: keyof T = "created" as keyof T,
  dateFormat: string = "YYYY-MM-DD",
  formatGroupKey?: DateGroupFormatter,
): Record<string, T[]> {
  if (!data || !Array.isArray(data) || data.length === 0) {
    return {};
  }

  const grouped: Record<string, T[]> = {};

  data.forEach((item) => {
    const timestamp = item[timestampField];

    // 跳过没有时间戳的项
    if (!timestamp || typeof timestamp !== "number") {
      return;
    }

    // 将时间戳转换为日期字符串作为分组 key
    const dateKey = dayjs(timestamp).format(dateFormat);

    // 如果提供了自定义格式化函数，使用它来生成分组 key
    const groupKey = formatGroupKey
      ? formatGroupKey(timestamp, dateKey)
      : dateKey;

    // 初始化该日期的数组
    if (!grouped[groupKey]) {
      grouped[groupKey] = [];
    }

    // 添加到对应日期的数组中
    grouped[groupKey].push(item);
  });

  return grouped;
}

/**
 * 获取分组后的日期列表，按时间倒序排列
 * @param groupedData 分组后的数据
 * @returns 日期字符串数组
 */
export function getGroupedDateKeys(
  groupedData: Record<string, any[]>,
): string[] {
  return Object.keys(groupedData).sort((a, b) => {
    return dayjs(b).valueOf() - dayjs(a).valueOf();
  });
}

/**
 * 获取指定日期的数据
 * @param groupedData 分组后的数据
 * @param dateKey 日期 key
 * @returns 该日期的数据数组
 */
export function getDataByDate<T>(
  groupedData: Record<string, T[]>,
  dateKey: string,
): T[] {
  return groupedData[dateKey] || [];
}
